import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { Deal, User } from '../../types'
import {
  transitionDealStatus,
  getAvailableActions,
  canUserTransition,
  permissionForTransition,
  isDestructiveTransition,
  statusLabel,
  statusColor,
  DealStatus,
} from '../../lib/dealStatusService'
import DealWorkspace from './DealWorkspace'
import './DealsView.css'

interface DealsViewProps {
  user: User
}

/**
 * §5 splits the pipeline in two.
 *
 * INFLOW is buying the car; OUTFLOW is selling it. They were one long board of
 * eight columns, which meant the sale stages were always off the right-hand edge
 * and a salesperson chasing stock and a salesperson chasing a buyer were looking
 * at the same crowded screen.
 *
 * `paid` sits at the end of INFLOW and the start of OUTFLOW deliberately — it is
 * the handover, and it should be visible from both sides.
 */
/* A stage carries an optional note so the board can explain itself. Typing the arrays keeps
   the ones without a note from making `stage.note` an error on the others. */
interface Stage {
  id: string
  label: string
  color: string
  note?: string
}

/*
 * A column's label and colour come from the canonical registry in
 * dealStatusService; only the note is this screen's own, because a note explains
 * what the column is FOR on this board and belongs to the board.
 *
 * These rows used to carry their own label and hex, which is how Stock
 * Management ended up disagreeing with this board about three of them.
 */
const stage = (id: string, note?: string): Stage => ({
  id,
  label: statusLabel(id),
  color: statusColor(id),
  ...(note ? { note } : {}),
})

const INFLOW_STAGES: Stage[] = [
  stage('sourced'),
  stage('under_offer'),
  stage('contract_signed'),
  stage('committed'),
  stage('approved_for_payment'),
  stage('paid'),
]

/*
 * Winnie, 10 Sep: Sold, Listed and Sold — Awaiting Payment come off the board.
 *
 * A sale is now either In Transit or Delivered, and the money side of it is read from the
 * Sales (AR) invoice report and the Debtor report rather than from a pipeline column, so
 * Awaiting Payment was a second copy of a figure that already lives somewhere better.
 *
 * The three statuses stay in the database. 153 imported deals sit on `sold`, and nothing
 * records whether they were delivered - no delivered_at, no delivery address - so moving
 * them into In Transit or Delivered would be inventing a fact. They keep their rows and they
 * keep appearing in Stock Management and in Sales (AR), which is where Winnie says the sold
 * book should now be read. They simply no longer take a column of their own, and the
 * transition map no longer lets anything new land on them.
 */
const OUTFLOW_STAGES: Stage[] = [
  stage('paid'),
  {
    ...stage('deal_pending'),
    note: 'A wholesale buyer has agreed to purchase and the sales representative has generated a sales proposal.',
  },
  {
    ...stage('sold_in_transit'),
    note: 'Pending delivery. Direct Auto handles shipping.',
  },
  {
    ...stage('sold_delivered'),
    note: 'The buyer handles shipping.',
  },
]

const ACTIVE_STAGES = [...INFLOW_STAGES, ...OUTFLOW_STAGES.slice(1)]

/* Which statuses actually get a column. The side counts have to be drawn from these
   and not from `pipelineSideOf`: that function still calls a retired `sold` or `listed`
   deal an outflow deal - correctly - but those stages no longer have a column, so
   counting them promised 154 cards on the Outflow tab and then drew an empty board. */
const idsOf = (ss: Stage[]) => new Set(ss.map((s) => s.id))
const INFLOW_IDS = idsOf(INFLOW_STAGES)
const OUTFLOW_IDS = idsOf(OUTFLOW_STAGES)
const ACTIVE_IDS = idsOf(ACTIVE_STAGES)
const RETIRED_IDS = new Set(['listed', 'sold', 'awaiting_payment'])

// Closed and Cancelled deals used to have no column at all, so the moment a deal
// reached either it vanished from the board with no filter and no count.
const CLOSED_STAGES: Stage[] = [
  stage('closed'),
  stage('cancelled'),
]

export default function DealsView({ user }: DealsViewProps) {
  const [deals, setDeals] = useState<Deal[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)
  const [workspaceDeal, setWorkspaceDeal] = useState<Deal | null>(null)
  const [banner, setBanner] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null)
  const [showClosed, setShowClosed] = useState(false)
  const [side, setSide] = useState<'inflow' | 'outflow' | 'all'>('inflow')
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<{ deal: Deal; to: DealStatus } | null>(null)

  const fetchDeals = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false })

      if (error) throw error
      setDeals(data || [])
      setLoadError(null)
    } catch (err) {
      // Previously console.error only, so a failed query rendered a healthy-looking
      // board with 0 in every column and the user concluded the pipeline was empty.
      console.error('Error fetching deals:', err)
      setLoadError(err instanceof Error ? err.message : 'Could not load the pipeline')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDeals()

    const subscription = supabase
      .channel('deals_pipeline_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'deals' },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setDeals((prev) => prev.filter((d) => d.id !== payload.old.id))
          } else {
            const next = payload.new as Deal
            if (next.deleted_at) {
              setDeals((prev) => prev.filter((d) => d.id !== next.id))
            } else {
              setDeals((prev) => [next, ...prev.filter((d) => d.id !== next.id)])
            }
          }
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [user.id, reloadKey, fetchDeals])

  // Escape closes whichever layer is on top.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (confirm) setConfirm(null)
      else if (menuFor) setMenuFor(null)
      else if (workspaceDeal) setWorkspaceDeal(null)
      else if (selectedDeal) setSelectedDeal(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [confirm, menuFor, workspaceDeal, selectedDeal])

  // Fall back across looked-up (autograb_*) and manual (vehicle_*) fields.
  const dYear = (d: Deal) => d.autograb_year ?? d.vehicle_year
  const dMake = (d: Deal) => d.autograb_make || d.vehicle_make || ''
  const dModel = (d: Deal) => d.autograb_model || d.vehicle_model || ''
  const dVin = (d: Deal) => d.vehicle_vin || d.autograb_vin
  const dSell = (d: Deal) => d.autograb_market_value ?? d.estimated_sale_price
  const money = (n?: number) => (n != null ? `$${Math.round(n).toLocaleString()}` : '—')

  const baseStages =
    side === 'inflow' ? INFLOW_STAGES : side === 'outflow' ? OUTFLOW_STAGES : ACTIVE_STAGES
  const stages = showClosed ? [...baseStages, ...CLOSED_STAGES] : baseStages
  const getDealsByStage = (stage: string) => deals.filter((d) => d.deal_status === stage)
  const closedCount = deals.filter((d) => d.deal_status === 'closed' || d.deal_status === 'cancelled').length

  /* Count only what the board will draw, so a tab never advertises cards it cannot show. */
  const countFor = (id: string) =>
    deals.filter((d) =>
      (id === 'inflow' ? INFLOW_IDS : id === 'outflow' ? OUTFLOW_IDS : ACTIVE_IDS).has(d.deal_status)
    ).length

  /* Deals still sitting on a stage Winnie retired on 10 Sep. They are not lost and they are
     not hidden - they are on Stock Management and on Sales (AR) - but they no longer have a
     column here, and saying so is better than leaving somebody to wonder where they went. */
  const retiredCount = deals.filter((d) => RETIRED_IDS.has(d.deal_status)).length

  const move = async (deal: Deal, newStage: DealStatus) => {
    const fromStatus = deal.deal_status as DealStatus
    if (fromStatus === newStage) return

    setBanner(null)
    setMenuFor(null)
    setConfirm(null)

    // Fail fast on permission so the card never visibly moves for a user who
    // was never allowed to make the move.
    if (!canUserTransition(user, fromStatus, newStage)) {
      setBanner({
        text: `You do not have permission to move a deal to ${statusLabel(newStage)} (requires ${permissionForTransition(fromStatus, newStage)}).`,
        tone: 'error',
      })
      return
    }

    const prevDeals = deals
    setDeals((prev) =>
      prev.map((d) => (d.id === deal.id ? { ...d, deal_status: newStage as Deal['deal_status'] } : d))
    )

    try {
      const userId = (await supabase.auth.getUser()).data.user?.id
      // Route through the status service so the move is validated against the
      // allowed transitions + preconditions (Framework B.1), enforces the
      // transition's permission, fires the workflow triggers, and is logged.
      const result = await transitionDealStatus(deal.id, fromStatus, newStage, userId, user)
      if (!result.success) setDeals(prevDeals) // snap the card back
      setBanner({ text: result.message, tone: result.success ? 'ok' : 'error' })
    } catch (err) {
      setDeals(prevDeals)
      setBanner({ text: err instanceof Error ? err.message : 'Could not move deal', tone: 'error' })
    }
  }

  const requestMove = (deal: Deal, to: DealStatus) => {
    if (isDestructiveTransition(to, deal.deal_status as DealStatus)) setConfirm({ deal, to })
    else move(deal, to)
  }

  const handleDrop = (dealId: string, stage: string) => {
    const deal = deals.find((d) => d.id === dealId)
    if (deal) requestMove(deal, stage as DealStatus)
  }

  return (
    <div className="view deals-view">
      <div className="view-header">
        <div className="view-title-section">
          <h1>Deal Pipeline</h1>
          <p>Drag a card between stages, or use the ⋯ menu on a card — both apply the same rules.</p>
        </div>
        <div className="pipeline-header-actions">
          <div className="pipeline-side" role="tablist" aria-label="Pipeline half">
            {([
              ['inflow', 'Inflow — buying'],
              ['outflow', 'Outflow — selling'],
              ['all', 'Both'],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={side === id}
                className={`pipeline-side-btn${side === id ? ' on' : ''}`}
                onClick={() => setSide(id)}
              >
                {label}
                <em>{countFor(id)}</em>
              </button>
            ))}
          </div>
          <button className="ghost-btn" onClick={() => setShowClosed((v) => !v)}>
            {showClosed ? 'Hide closed & cancelled' : `Show closed & cancelled (${closedCount})`}
          </button>
          <button className="ghost-btn" onClick={() => setReloadKey((k) => k + 1)}>Refresh</button>
        </div>
      </div>

      {loadError && (
        <div className="pipeline-banner error">
          <span>{loadError}</span>
          <button className="ghost-btn" onClick={() => setReloadKey((k) => k + 1)}>Retry</button>
        </div>
      )}

      {banner && (
        <div
          className={`pipeline-banner ${banner.tone === 'error' ? 'error' : 'ok'}`}
          onClick={() => setBanner(null)}
          role={banner.tone === 'error' ? 'alert' : 'status'}
        >
          <span>{banner.text}</span>
          <span className="banner-dismiss">✕</span>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading pipeline...</div>
      ) : (
        <>
        {retiredCount > 0 && (
          <div className="pipeline-retired-note">
            {retiredCount} deal{retiredCount === 1 ? '' : 's'} came across on the old <strong>Sold</strong>,{' '}
            <strong>Listed</strong> or <strong>Sold &mdash; Awaiting Payment</strong> stages, which no longer have a
            column. Nothing has been deleted: they are still on Stock Management, on Sales (AR) and in the
            Debtor report, and the Margin Scheme register still counts every one of them.
          </div>
        )}
        <div className="board">
          {stages.map((stage) => {
            const stageDeals = getDealsByStage(stage.id)
            return (
              <div
                key={stage.id}
                className="col"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  const draggedId = e.dataTransfer.getData('dealId')
                  if (draggedId) handleDrop(draggedId, stage.id)
                }}
              >
                <div className="col-h">
                  <span className="col-dot" style={{ background: stage.color }}></span>
                  {stage.label}
                  <span className="cnt">{stageDeals.length}</span>
                </div>

                {/* Winnie, 10 Sep: the three stages people kept asking about say what they
                    mean on the board itself, rather than in a document nobody has open. */}
                {stage.note ? <div className="col-note">{stage.note}</div> : null}

                <div className="col-cards">
                  {stageDeals.length === 0 && <div className="col-empty">Nothing here</div>}
                  {stageDeals.map((deal) => {
                    const mp = deal.margin_percent
                    const targets = getAvailableActions(deal.deal_status as DealStatus)
                    return (
                      <div
                        key={deal.id}
                        className="pcard"
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move'
                          e.dataTransfer.setData('dealId', deal.id)
                        }}
                      >
                        <button
                          className="pc-menu-btn"
                          title="Move this deal"
                          aria-label={`Move ${dMake(deal)} ${dModel(deal)}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            setMenuFor(menuFor === deal.id ? null : deal.id)
                          }}
                        >
                          ⋯
                        </button>

                        {menuFor === deal.id && (
                          <div className="pc-menu" onClick={(e) => e.stopPropagation()}>
                            <div className="pc-menu-h">Move to</div>
                            {targets.length === 0 && <div className="pc-menu-empty">No further stages</div>}
                            {targets.map((t) => {
                              const allowed = canUserTransition(user, deal.deal_status as DealStatus, t)
                              return (
                                <button
                                  key={t}
                                  className={`pc-menu-item${isDestructiveTransition(t, deal.deal_status as DealStatus) ? ' danger' : ''}`}
                                  disabled={!allowed}
                                  title={allowed ? undefined : `Requires ${permissionForTransition(deal.deal_status as DealStatus, t)}`}
                                  onClick={() => requestMove(deal, t)}
                                >
                                  {statusLabel(t)}
                                </button>
                              )
                            })}
                            <div className="pc-menu-sep" />
                            <button className="pc-menu-item" onClick={() => { setMenuFor(null); setWorkspaceDeal(deal) }}>
                              Open workspace
                            </button>
                          </div>
                        )}

                        <div className="pc-body" onClick={() => setSelectedDeal(deal)}>
                          <div className="pc-t">
                            {dYear(deal)} {dMake(deal)} {dModel(deal)}
                          </div>
                          <div className="pc-m">
                            {money(deal.valuation_amount)} → {money(dSell(deal))}
                          </div>
                          <div className="pc-f">
                            {mp != null && (
                              <span className={`mini-tag margin-tag margin-${mp >= 20 ? 'good' : mp >= 10 ? 'ok' : 'poor'}`}>
                                {mp.toFixed(1)}% margin
                              </span>
                            )}
                            <span className="pc-val">{money(deal.gross_margin)}</span>
                          </div>
                          <div className="statusline">
                            <i>●</i> {deal.seller_name}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
        </>
      )}

      {confirm && (
        <div className="detail-panel" onClick={() => setConfirm(null)}>
          <div className="confirm-card" onClick={(e) => e.stopPropagation()} role="alertdialog">
            <h3>Cancel this deal?</h3>
            <p>
              {dYear(confirm.deal)} {dMake(confirm.deal)} {dModel(confirm.deal)} will move to{' '}
              <strong>{statusLabel(confirm.to)}</strong>. A cancelled deal has no valid next stage
              and cannot be reopened from the pipeline.
            </p>
            <div className="confirm-actions">
              <button className="ghost-btn" onClick={() => setConfirm(null)}>Keep the deal</button>
              <button className="ghost-btn danger" onClick={() => move(confirm.deal, confirm.to)}>
                Yes, cancel it
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedDeal && (
        <div className="detail-panel" onClick={() => setSelectedDeal(null)}>
          <div className="detail-card" onClick={(e) => e.stopPropagation()}>
            <button className="detail-close" onClick={() => setSelectedDeal(null)}>
              ✕
            </button>
            <h2>
              {dYear(selectedDeal)} {dMake(selectedDeal)} {dModel(selectedDeal)}
            </h2>
            <div className="detail-grid">
              <div className="detail-row">
                <span className="label">VIN</span>
                <span className="value">{dVin(selectedDeal) || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="label">Purchase Price</span>
                <span className="value">{money(selectedDeal.valuation_amount)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Market Value</span>
                <span className="value">{money(dSell(selectedDeal))}</span>
              </div>
              <div className="detail-row">
                <span className="label">Margin</span>
                <span className="value">
                  {money(selectedDeal.gross_margin)}
                  {selectedDeal.margin_percent != null ? ` (${selectedDeal.margin_percent.toFixed(1)}%)` : ''}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Seller</span>
                <span className="value">{selectedDeal.seller_name}</span>
              </div>
              {selectedDeal.seller_email && (
                <div className="detail-row">
                  <span className="label">Email</span>
                  <span className="value">{selectedDeal.seller_email}</span>
                </div>
              )}
              {selectedDeal.seller_phone && (
                <div className="detail-row">
                  <span className="label">Phone</span>
                  <span className="value">{selectedDeal.seller_phone}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="label">Status</span>
                <span className="value">{statusLabel(selectedDeal.deal_status)}</span>
              </div>
              <div className="detail-row">
                <span className="label">PPSR</span>
                <span className="value">{selectedDeal.ppsr_status || 'Not checked'}</span>
              </div>
            </div>
            {selectedDeal.notes && (
              <div className="detail-notes">
                <h3>Notes</h3>
                <p>{selectedDeal.notes}</p>
              </div>
            )}
            {/* The pipeline is where people live, and this modal used to be a
                read-only cul-de-sac whose only control was ✕. */}
            <div className="detail-actions">
              <button
                className="ghost-btn primary"
                onClick={() => {
                  const d = selectedDeal
                  setSelectedDeal(null)
                  setWorkspaceDeal(d)
                }}
              >
                Open workspace
              </button>
            </div>
          </div>
        </div>
      )}

      {workspaceDeal && (
        <DealWorkspace
          user={user}
          deal={workspaceDeal}
          onClose={() => setWorkspaceDeal(null)}
          onChanged={() => setReloadKey((k) => k + 1)}
        />
      )}
    </div>
  )
}
